import React, { useState } from "react";
import { Button, RefreshIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Preloader from "~/components/Preloader";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetUserList } from "~/entity/Users/hooks/useGetUserList";
import { useUsers } from "~/entity/Users/hooks/useUsers";
import {
  orderDirectionIProps,
  userListFilterIProps,
} from "~/entity/Users/model/schema";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { useImportUserMutation } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { UsersFilter, useUsersFilterValues } from "~/widgets/Filter";

import ModalsComponent from "./components/ModalsComponent";
import TableUserList from "./components/TableUserList";
import { MigrateUser } from "./components/MigrateToStaffModal";
import { orderByChange } from "./options";

import s from "./Users.less";

const Users = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const setData = useSetLocationData();
  const { page, count, sortField, sortType, searchText, openModal } =
    useGetLocationData();
  const [selectedUsers, setSelectedUsers] = useState<MigrateUser[]>([]);
  const [isExportingUserList, setIsExportingUserList] = useState(false);
  const { hasPermission } = usePermissions();
  const filterValues = useUsersFilterValues();
  const { exportUserList } = useUsers();
  const { notifySuccess, notifyError } = useNotify();

  const filterParams: userListFilterIProps = {
    orderDirection: sortType
      ? (sortType.toLowerCase() as orderDirectionIProps)
      : orderDirectionIProps.asc,
    // searchText из общего поиска приоритетнее, иначе берём из формы фильтра.
    searchText: searchText || filterValues.searchText,
    orderBy: orderByChange(sortField) as userListFilterIProps["orderBy"],
    id: filterValues.id,
    fullName: filterValues.fullName,
    position: filterValues.position,
    email: filterValues.email,
    phone: filterValues.phone,
    dealerIds: filterValues.dealerIds,
    roleNames: filterValues.roleNames,
    permissionIds: filterValues.permissionIds,
    subdivisionIds: filterValues.subdivisionIds,
    status: filterValues.status || filterValues.statuses?.[0],
  };

  const {
    data: dataUserList,
    isLoading: isLoadingUserList,
    refetch: refetchUserList,
  } = useGetUserList({ page, count, ...filterParams });

  const [importUserMutation, importUser] = useImportUserMutation({
    onCompleted() {
      notifySuccess(t("common.import_completed"));
      refetchUserList();
    },
  });

  const selectedIds = selectedUsers.map((user) => user.id);

  const handleExportUserList = async () => {
    try {
      setIsExportingUserList(true);
      const { data } = await exportUserList({ ...filterParams, count: 1000 });
      if (!data?.link) return;
      const a = document.createElement("a");
      a.href = data.link;
      a.setAttribute("download", "");
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      notifyError(t("common.errorMessages.default"));
    } finally {
      setIsExportingUserList(false);
    }
  };

  const canRoles = hasPermission("users.access.roles");
  const canExport = hasPermission("user.getUsers");

  const onToggleSelect = (record: {
    id?: string | number;
    fullName?: string;
  }) => {
    const id = Number(record.id);
    setSelectedUsers((prev) =>
      prev.some((user) => user.id === id)
        ? prev.filter((user) => user.id !== id)
        : [...prev, { id, fullName: record.fullName || "" }],
    );
  };

  return (
    <Wrapper.Content
      title={t("users.USER_REGISTER")}
      controlComponent={
        canRoles || canExport ? (
          <div className={s.controlsBlock}>
            {canRoles && (
              <Button
                bold
                color="secondary"
                fontSize="12"
                size="medium"
                onClick={() => history.push("/registry_of_role")}
              >
                {t("ui.roles")}
              </Button>
            )}
            {canExport && (
              <Button
                bold
                color="secondary"
                fontSize="12"
                size="medium"
                loading={isExportingUserList}
                onClick={handleExportUserList}
              >
                {t("ui.download")}
              </Button>
            )}
          </div>
        ) : undefined
      }
      filterComponent={<UsersFilter />}
    >
      <div className={s.toolbar}>
        {hasPermission("users.importUser") && (
          <Button
            bold
            color="secondary"
            variant="filled"
            fontSize="10"
            size="small"
            radius={30}
            icon={<RefreshIcon size={12} />}
            loading={importUser.loading}
            onClick={() => importUserMutation()}
          >
            {t("users.sync")}
          </Button>
        )}
        <div className={s.sortSlot}>
          {/* TODO PASDILER-2428: перенос пользователей в сотрудники временно скрыт до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
          {selectedUsers.length > 0 && (
            <Button
              bold
              color="secondary"
              fontSize="10"
              size="small"
              radius={30}
              onClick={() => setData("openModal", "Migrate")}
            >
              {t("users.migrate")}
            </Button>
          )} */}
        </div>
      </div>
      {!isLoadingUserList ? (
        <>
          <TableUserList
            dataUserList={dataUserList}
            isLoadingUserList={isLoadingUserList}
            hasPermission={hasPermission}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
          />
          <ModalsComponent
            dataUserList={dataUserList}
            refetchUserList={refetchUserList}
          />
          {/* TODO PASDILER-2428: модалка переноса пользователей временно скрыта до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
          <MigrateToStaffModal
            open={openModal === "Migrate"}
            users={selectedUsers}
            onClose={() => setData("openModal", "")}
            onSuccess={() => {
              setSelectedUsers([]);
              refetchUserList();
            }}
          /> */}
        </>
      ) : (
        <Preloader />
      )}
    </Wrapper.Content>
  );
};

export default Users;
